import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "./index";

// Translation of text that comes from the API (event titles, sub texts,
// session type names, topics, level labels ...).
//
// The English text itself is the key in locales/<lng>/dynamicContent.json, so
// there is nothing to add for English. A text that isn't in the file is shown
// exactly as the API sent it, so a new or reworded title never breaks.
//
// Lookup order:
//   1. the whole text ("Messages from Spirit – A Live Online Demonstration")
//   2. each part between " – ", " - " or " | " separators
//      ("Messages from Spirit" + "A Live Online Demonstration")
//   3. phrases from dynamicContent.json found INSIDE the part, longest first
//      ("Please arrive a few minutes early" -> uses "a few minutes early")
//   4. last resort, for whatever is left: common words one by one
//      from locales/<lng>/dynamicWords.json ("a", "is", "with", "online" ...).
//      Word order and grammar are NOT fixed by this, so prefer adding the whole
//      phrase to dynamicContent.json for anything that is shown often.
export const DYNAMIC_NS = "dynamicContent";
export const DYNAMIC_WORDS_NS = "dynamicWords";

const LOOKUP_OPTIONS = { ns: DYNAMIC_NS, keySeparator: false, nsSeparator: false };
const SEPARATOR = /(\s+[–—-]\s+|\s*\|\s*)/; // the capture group keeps the separators

const normalize = (text) => String(text).replace(/\s+/g, " ").trim();

const lookup = (i18n, text) => {
  const key = normalize(text);
  if (!key) return null;
  return i18n.exists(key, LOOKUP_OPTIONS) ? i18n.t(key, LOOKUP_OPTIONS) : null;
};

// A word: letters, optionally with an apostrophe ("don't", "spirit's").
const WORD = /([A-Za-z]+(?:['\u2019][A-Za-z]+)?)/;

// English capitalises month and day names, French and Spanish don't: keep the
// dictionary's own case for these ("March" -> "mars", "März").
const KEEP_DICTIONARY_CASE = [
  "january", "february", "march", "april", "may", "june", "july",
  "august", "september", "october", "november", "december",
  "jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  // units: "30 Min" -> "30 min" (fr/es), "30 Min." (de)
  "min", "mins",
];

const matchCase = (source, translated) => {
  if (KEEP_DICTIONARY_CASE.includes(source.toLowerCase())) return translated;
  if (source[0] === source[0].toUpperCase() && source.slice(1) === source.slice(1).toLowerCase()) {
    return translated.charAt(0).toUpperCase() + translated.slice(1);
  }
  return translated;
};

// Month and day names are translated in any case: "OCT", "Oct", "oct", "October".
const findCalendarWord = (i18n, lng, word) => {
  const lower = word.toLowerCase();
  if (!KEEP_DICTIONARY_CASE.includes(lower)) return null;
  // Written all lowercase these are usually other words ("you may", "mar the").
  if (word === lower && ["may", "mar", "jan"].includes(lower)) return null;
  const capitalised = lower.charAt(0).toUpperCase() + lower.slice(1);
  const found = [capitalised, lower]
    .map((key) => i18n.getResource(lng, DYNAMIC_WORDS_NS, key))
    .find((value) => typeof value === "string" && value);
  if (!found) return null;
  return word.length > 1 && word === word.toUpperCase() ? found.toUpperCase() : found;
};

// ---------------------------------------------------------------------------
// Missing-word tracker (development builds only).
// Every word that had no translation is recorded per language. In the browser
// console run:   verysoulMissingWords()        -> list for the current language
//                copy(verysoulMissingWords())  -> copies it, one word per line
//                verysoulMissingWords("de")    -> list for another language
//                verysoulMissingWords.clear()  -> start a fresh list
// Add the words to locales/<lng>/dynamicWords.json (or scripts/add-dynamic-words.js).
// ---------------------------------------------------------------------------
const TRACK_MISSING = process.env.NODE_ENV !== "production";
const missingWords = {};

const recordMissing = (lng, word) => {
  if (!TRACK_MISSING) return;
  if (!missingWords[lng]) missingWords[lng] = new Set();
  missingWords[lng].add(word);
};

if (TRACK_MISSING && typeof window !== "undefined") {
  const list = (lng) => {
    const code = lng || (window.localStorage && window.localStorage.getItem("i18nextLng")) || "";
    return Array.from(missingWords[code] || []).sort((a, b) => a.localeCompare(b)).join("\n");
  };
  list.clear = () => {
    Object.keys(missingWords).forEach((key) => delete missingWords[key]);
  };
  window.verysoulMissingWords = list;
}

export const getMissingWords = (lng) => Array.from(missingWords[lng] || []).sort();

const translateWord = (i18n, lng, word) => {
  const calendarWord = findCalendarWord(i18n, lng, word);
  if (calendarWord) return calendarWord;
  // Skip acronyms and all-caps words ("AM", "PM", "USA", "Q&A"): they are
  // times or names, not the common words in the list.
  if (word.length > 1 && word === word.toUpperCase()) return word;
  // An exact-case key wins ("May" the month), then the lowercase key ("march").
  let translated = i18n.getResource(lng, DYNAMIC_WORDS_NS, word);
  if (typeof translated !== "string") {
    translated = i18n.getResource(lng, DYNAMIC_WORDS_NS, word.toLowerCase());
  }
  if (typeof translated === "string" && translated) return matchCase(word, translated);
  // Capitalised words in the middle of a text are mostly names ("Karina",
  // "VerySoul"); they are recorded too, marked with a leading "*".
  recordMissing(lng, word === word.toLowerCase() ? word : `*${word}`);
  return word;
};

// Only stand-alone words are translated: "Q&A", "1-on-1", "A/B" stay as they are.
const FREE_BEFORE = /(^|[\s("\u201C])$/;
const FREE_AFTER = /^($|[\s)"\u201D,.:;!?])/;

const translateWords = (i18n, lng, text) => {
  const pieces = text.split(WORD);
  return pieces
    .map((piece, index) => {
      if (index % 2 === 0) return piece; // text between words
      const standalone = FREE_BEFORE.test(pieces[index - 1]) && FREE_AFTER.test(pieces[index + 1]);
      if (!standalone) return piece;
      // At the end of a phrase "her" is the object ("with her." -> "avec elle"),
      // otherwise possessive ("her presence" -> "sa présence"). Same for "his".
      const endsPhrase = index + 2 >= pieces.length || /[,.:;!?)]/.test(pieces[index + 1]);
      const objectForm = endsPhrase
        ? i18n.getResource(lng, DYNAMIC_WORDS_NS, `${piece.toLowerCase()} (object)`)
        : null;
      const translated =
        typeof objectForm === "string" && objectForm
          ? matchCase(piece, objectForm)
          : translateWord(i18n, lng, piece);
      // "Oct." -> "oct." not "oct..": the source already has the dot.
      return translated.endsWith(".") && pieces[index + 1].startsWith(".")
        ? translated.slice(0, -1)
        : translated;
    })
    .join("");
};

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Multi-word phrases of the current language, longest first, as one regex.
// Cached per language and rebuilt when the loaded file changes.
const phraseCache = {};
const getPhraseMatcher = (i18n, lng) => {
  const bundle = i18n.getResourceBundle(lng, DYNAMIC_NS) || {};
  const keys = Object.keys(bundle).filter(
    (key) => typeof bundle[key] === "string" && /[A-Za-z]\s+[A-Za-z]/.test(key)
  );
  const cached = phraseCache[lng];
  if (cached && cached.bundle === bundle && cached.size === keys.length) return cached;

  const byLower = {};
  keys.forEach((key) => {
    byLower[normalize(key).toLowerCase()] = bundle[key];
  });
  const sorted = keys.slice().sort((a, b) => b.length - a.length);
  // No look-behind (older Safari can't run it): the leading group keeps the
  // character before the phrase so it can be put back.
  const regex = sorted.length
    ? new RegExp(
        "(^|[^A-Za-z])(" +
          sorted.map((key) => escapeRegExp(normalize(key)).replace(/ /g, "\\s+")).join("|") +
          ")(?=$|[^A-Za-z])",
        "gi"
      )
    : null;
  phraseCache[lng] = { bundle, size: keys.length, regex, byLower };
  return phraseCache[lng];
};

const translatePhrasesInside = (i18n, lng, text) => {
  const { regex, byLower } = getPhraseMatcher(i18n, lng);
  if (!regex) return translateWords(i18n, lng, text);

  let result = "";
  let last = 0;
  regex.lastIndex = 0;
  let match = regex.exec(text);
  while (match) {
    const start = match.index + match[1].length;
    const phrase = match[2];
    const translated = byLower[normalize(phrase).toLowerCase()];
    result += translateWords(i18n, lng, text.slice(last, start));
    // Capitalise the translation only where a sentence starts; mid-sentence the
    // dictionary's own case is kept ("(30 Min each)" -> "(30 min chacun)").
    const startsSentence = /(^|[.!?:])\s*$/.test(text.slice(0, start));
    const capitalise =
      startsSentence && phrase[0] === phrase[0].toUpperCase() && phrase[0] !== phrase[0].toLowerCase();
    result +=
      typeof translated === "string"
        ? capitalise
          ? translated.charAt(0).toUpperCase() + translated.slice(1)
          : translated
        : phrase;
    last = start + phrase.length;
    regex.lastIndex = last;
    match = regex.exec(text);
  }
  return result + translateWords(i18n, lng, text.slice(last));
};

const translatePart = (i18n, lng, part) => {
  const phrase = lookup(i18n, part);
  return phrase !== null ? phrase : translatePhrasesInside(i18n, lng, part);
};

export const translateDynamic = (i18n, text) => {
  if (typeof text !== "string" || !text.trim()) return text;
  const lng = i18n && (i18n.resolvedLanguage || i18n.language);
  if (!lng || lng === "en") return text;

  const whole = lookup(i18n, text);
  if (whole !== null) return whole;

  return text
    .split(SEPARATOR)
    .map((part, index) => (index % 2 === 1 ? part : translatePart(i18n, lng, part)))
    .join("");
};

// "English" (as stored in the DB) -> "anglais" / "inglés" / "Englisch".
export const translateLanguageName = (i18n, name) => {
  if (typeof name !== "string" || !name.trim()) return name;
  const lng = i18n && (i18n.resolvedLanguage || i18n.language);
  if (!lng || lng === "en") return name;
  const meta = LANGUAGES.find((l) => l.english.toLowerCase() === name.trim().toLowerCase());
  if (meta && typeof Intl !== "undefined" && Intl.DisplayNames) {
    try {
      const label = new Intl.DisplayNames([lng], { type: "language" }).of(meta.code);
      if (label && label !== meta.code) return label;
    } catch (e) {
      // fall through to the dictionary
    }
  }
  return translateDynamic(i18n, name);
};

// Note: no "??" or "?." in this file: the app's webpack 4 build can't parse them.
// Date parts for display only. English keeps the exact strings the page
// already built with moment, so nothing changes for English users.
export const formatDisplayDate = (i18n, value, options) => {
  const lng = i18n && (i18n.resolvedLanguage || i18n.language);
  if (!value || !lng || lng === "en") return null;
  let date = value;
  if (!(value instanceof Date)) {
    // "YYYY-MM-DD" is read as a local date (new Date() would read it as UTC
    // and can show the previous day).
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    date = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  }
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(lng, options).format(date);
  } catch (e) {
    return null;
  }
};

// Re-renders the component when the language changes.
export const useDynamicTranslation = () => {
  const { t, i18n } = useTranslation(["common", DYNAMIC_NS]);
  const lng = i18n.resolvedLanguage || i18n.language;

  const tc = useCallback((text) => translateDynamic(i18n, text), [i18n, lng]); // eslint-disable-line react-hooks/exhaustive-deps
  const tLanguage = useCallback((name) => translateLanguageName(i18n, name), [i18n, lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // "3 spots left(of 10)" from the API -> translated, same numbers.
  const tSpots = useCallback(
    (text) => {
      if (typeof text !== "string" || lng === "en") return text;
      const match = text.match(/^\s*(\d+)\s+spots?\s+left\s*\(\s*of\s+(\d+)\s*\)\s*$/i);
      return match
        ? t("common:common.spotsLeft", { count: Number(match[1]), total: Number(match[2]) })
        : text;
    },
    [t, lng]
  );

  const formatDate = useCallback(
    (value, options, fallback) => {
      const formatted = formatDisplayDate(i18n, value, options);
      return formatted !== null ? formatted : fallback;
    },
    [i18n, lng] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return { tc, tLanguage, tSpots, formatDate };
};
